import { DialogCloseResult } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	bindingMode,
	computedFrom,
	newInstance,
	observable,
	TaskQueue,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController } from 'aurelia-validation';
import debounce from 'lodash-es/debounce';
import PerfectScrollbar from 'perfect-scrollbar';

import { AuditApi } from '~/api/audit-api';
import { ClassicFormsApi } from '~/api/classic-forms-api';
import { NotesApi } from '~/api/notes-api';
import { BaseViewModel } from '~/base-view-model';
import { AcurityTableConfig } from '~/components/acurity-table/acurity-table';
import { AuditDialog, AuditDialogModel } from '~/components/audit/audit-dialog';
import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import { CustomisationConfig } from '~/components/customisation/customisation-config';
import {
	NotesDialog,
	NotesDialogModel,
	NotesDialogResult,
} from '~/components/notes/notes-dialog';
import { Notification } from '~/components/notification/notification';
import { DEBOUNCE_RECORD_SELECT_DURATION, ShortcutKey } from '~/constants';
import ClassicForm from '~/forms/classic-form';
import { Entity } from '~/models/column-definition';
import { FormModel } from '~/models/form-model';
import { FormParameters } from '~/models/form-parameters';
import { InsertResult } from '~/models/insert-result';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import { focusable } from '~/utils/focus';
import FormModelUtils from '~/utils/form-model-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { getSharedLabels } from '~/utils/shared-labels';

/**
 * Wrap slideout into Aurelia component so we can use Aurelia features
 */
@focusable
@autoinject
export class Slideout extends BaseViewModel {
	public static readonly Events = {
		Any: 'Slideout.Any', // Any slideout event (substitutes 'slideout-event')
		Attached: 'Slideout.Attached',
		Edit: 'Slideout.Edit',
		Delete: 'Slideout.Delete',
		DiscardChanges: 'Slideout.DiscardChanges',
		CloseRecord: 'Slideout.CloseRecord',
		CancelEdit: 'Slideout.CancelEdit',
		PreSave: 'Slideout.PreSave',
		PostSave: 'Slideout.PostSave',
	};

	public element: Element; // set by @focusable

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	public recordForm: Element;
	@bindable public parentForm: ClassicForm;

	private classicPanel: ClassicPanel;
	public eventSubscriptions: Subscription[] = [];

	@bindable({ defaultBindingMode: bindingMode.toView })
	private areStandardEditButtonsHidden: boolean = false;
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private canClick: boolean = true;
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private enableDelete: boolean = true;
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private fullHeight: boolean; // optional

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private functionName: string;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'recordIdChanged',
	})
	private recordId: number;

	@bindable private notesTitle: string;
	@bindable public title;
	// optional, used for normal tables where formModel is not set by api call
	@bindable public tableDataSet: Array<Entity<any>>;

	// optional, used for Pension Summary where slideout extends outside expanding card
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	public footerTooltipPlacement: string = 'top';

	private jQueryReference: JQuery<HTMLElement>;

	private recordHasNotes: boolean = false;

	private slideoutPS: PerfectScrollbar;
	private slideoutBodySelector: string = '.i-slideout__body';

	private slideoutId: string = 'selected-record-slideout';
	private isSlideoutShown: boolean = false;

	@bindable({ defaultBindingMode: bindingMode.toView })
	protected isContentLoading: boolean = false;

	@bindable protected parentId: string;

	@observable({ changeHandler: 'refSlideoutElChanged' })
	private refSlideoutEl: HTMLElement;

	private refSlideoutBody?: HTMLElement;

	/**
	 * The width of the slideout using percent
	 */
	private slideoutWidth: number = 0;

	private isSaving: boolean = false;

	private hasAuditDetailsOpen: boolean = false;

	private readonly sharedLabel = getSharedLabels();

	private readonly refFooter: HTMLElement;

	@bindable({ defaultBindingMode: bindingMode.oneTime })
	private auditDialogColumnNames: string[];

	@bindable({ defaultBindingMode: bindingMode.oneTime })
	private auditDialogTableConfig?: AcurityTableConfig;

	@bindable({ defaultBindingMode: bindingMode.toView })
	private keyboardShortCutOverride?: (context: any) => void;

	private queryParams: Map<string, string>;

	constructor(
		eventAggregator: EventAggregator,
		private taskQueue: TaskQueue,
		private classicFormsApi: ClassicFormsApi,
		@newInstance() private validationController: ValidationController,
		i18n: I18N,
		readonly notesApi: NotesApi,
		readonly auditApi: AuditApi,
		readonly dialogUtils: DialogUtils,
	) {
		super();
		this.i18n = i18n;
		this.eventAggregator = eventAggregator;
	}

	@computedFrom('classicPanel.formModel')
	get formModel(): FormModel | undefined {
		if (this.classicPanel) {
			return this.classicPanel.formModel;
		}
		return undefined;
	}

	@computedFrom('parentForm.customisationConfig')
	get customisationConfig(): CustomisationConfig | undefined | false {
		if (
			!this.parentForm ||
			this.parentForm.customisationConfig === undefined
		) {
			return undefined;
		}
		return this.parentForm.customisationConfig;
	}

	@computedFrom(
		'parentForm.isRecordDeleted',
		'parentForm.isUnDeletedAvailable',
	)
	get toggleDeleteButtonLabel(): string {
		if (
			this.parentForm.isRecordDeleted &&
			this.parentForm.isUnDeletedAvailable
		) {
			return this.i18n.tr('clickable-elements.un-delete');
		}
		return this.i18n.tr('clickable-elements.delete');
	}

	public attached() {
		if (!this.element) {
			throw new Error('Missing this.element property on slideout.');
		}

		// PerfectScrollbar
		if (!this.slideoutPS && this.refSlideoutBody) {
			Logger.log(
				'Setting slideout PerfectScrollbar',
				`${this.slideoutBodySelector}`,
			);
			this.slideoutPS = new PerfectScrollbar(this.refSlideoutBody);
		}

		this.publishEvent(Slideout.Events.Attached, { slideout: this });
		// once slideout attached, disable edit mode in classic panel
		this.classicPanel.setIsEditing(false);

		if (this.fullHeight && this.refSlideoutEl) {
			this.setSlideoutTop();
		}
	}

	public detached() {
		// NOTE: Jake: 2019-12-12
		// We close the slideout on detached() so that we trigger detachKeyboardShortcuts().
		this.setIsOpen(false);

		if (this.slideoutPS) {
			this.slideoutPS.destroy();
			this.slideoutPS = undefined;
		}
	}

	// attachKeyboardShortcuts is called when the slideout is opened, not when it's attached/invisible off-screen.
	// we don't want keyboard shortcuts to work if the slideout isn't visible on screen.
	private attachKeyboardShortcuts() {
		if (!this.element || !this.refFooter) {
			// Ignore if not yet rendered
			return;
		}

		// NOTE(Jake): 2018-10-05
		// Shortcuts from 'classic-form' are still available in this scope if we don't explicitly override
		// them.
		if (this.parentForm.hasNotes) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Notes,
				this.element.querySelectorAll(
					'[click\\.delegate="showNotesDialog()"]',
				),
			);
		}
		if (this.parentForm.hasAuditDetails) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Audit,
				this.element.querySelectorAll('[id="slideoutAuditDetails"]'),
			);
		}
		if (this.parentForm.canSave) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Save,
				this.element.querySelectorAll('[click\\.delegate="save()"]'),
			);
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Edit,
				this.element.querySelectorAll('[click\\.delegate="edit()"]'),
			);
		}
		if (this.isToggleDeleteButtonVisible) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Delete,
				this.element.querySelectorAll(
					'[click\\.delegate="buttonToggleDelete()"]',
				),
			);
		}
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Close,
			this.refFooter.querySelectorAll('[click\\.delegate="close()"]'),
		);
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Close,
			this.element.querySelectorAll(
				'[click\\.delegate="cancelEditOrClose()"]',
			),
		);

		if (this.keyboardShortCutOverride) {
			this.keyboardShortCutOverride(this);
		}
	}

	// attachKeyboardShortcuts is called when the slideout is opened, not when it's attached/invisible off-screen
	private detachKeyboardShortcuts() {
		if (!this.element) {
			// Ignore if not yet rendered
			return;
		}
		KeyboardShortcut.unsubscribeAll(this);
	}

	/**
	 * setIsEditing allows you to set whether the fields in the panel are editable or not.
	 * If false, all fields become readonly.
	 */
	public setIsEditing(enable: boolean) {
		this.classicPanel.setIsEditing(enable);
	}

	public checkPanelForChanges(): boolean {
		return this.classicPanel.checkPanelForChanges();
	}

	/**
	 * @param hasChanges provide pre-calculated dirty check result
	 */
	public dirtyCheckWithDialog(hasChanges?: boolean): Promise<void> {
		return this.classicPanel.promptToClosePanelIfChanged(hasChanges);
	}

	private recordIdChanged(newRecordId, oldRecordId): void {
		if (newRecordId) {
			this.updateTooltip();
			// NOTE: Jake: 2019-06-11 - WEBUI-2075
			// This logic used to be on the "open()" method, however we actually
			// want this to fire when the record selected is changed.
			// We use this data to see if "Notes" are set or not.
			if (this.parentForm.hasNotes && this.isFetchNotes) {
				this.loadNotesDebounced();
			}
		}
	}

	public setFormModel(model: FormModel) {
		this.classicPanel.formModel = model;
	}

	public async reload() {
		return this.classicPanel.populateWithClassicFormData(
			this.classicPanel.getFunctionName(),
			this.recordId,
		);
	}

	@computedFrom(
		'isSaveButtonVisible',
		'isSaving',
		'classicPanel.isInEditMode',
	)
	public get canSave(): boolean {
		return (
			(this.areStandardEditButtonsHidden || this.isSaveButtonVisible) &&
			!this.isSaving &&
			this.classicPanel.isInEditMode
		);
	}

	/**
	 * In member beneficiaries table, there will be -1 recordId exsiting for new dependent
	 */
	@computedFrom('recordId')
	public get isFetchNotes(): boolean {
		return this.recordId >= 0;
	}

	/**
	 * This sets the slideout to the saving state, does the save with the given save function (or the default save
	 * function if no function is given), then sets the slideout to the normal (non-saving) state. It also triggers the
	 * PreSave and PostSave events for the slideout.
	 *
	 * The default save function uses the composed classic panel to save and gives feedback to the user.
	 *
	 * @param saveFunction the given save function
	 */
	public save(saveFunction: () => Promise<void> = this.defaultSaveFunction) {
		if (!this.canSave) {
			if (DEVELOPMENT) {
				throw new Error(
					'Development Mode Error: save() should not be called if "canSave" is false. Wrap calls to save() in a "canSave" if-statement.',
				);
			}
			return;
		}

		this.isSaving = true;
		this.publishEvent(Slideout.Events.PreSave);
		saveFunction().finally(() => {
			this.isSaving = false;
			this.publishEvent(Slideout.Events.PostSave, {
				source: this.functionName,
			});
		});
	}

	private defaultSaveFunction: () => Promise<void> = () => {
		return this.classicPanel
			.save()
			.then((result) => {
				Notification.success(
					this.i18n.tr(
						'successes.this-record-was-successfully-updated',
					),
				);
				this.classicPanel.setIsEditing(false);
			})
			.catch((error) => {
				const message = FetchUtils.normalizeError(error);
				const errorMessage = this.i18n.tr(
					'errors.unable-to-update-this-record-message',
					{ message },
				);
				FetchUtils.logAndDisplayError(errorMessage);
			});
	};

	public enableReadonlyMode() {
		Logger.log('Set form to READ ONLY mode');
		this.classicPanel.setIsEditing(false);
	}

	public enableEditMode() {
		Logger.log('Set form to EDIT mode');
		this.classicPanel.setIsEditing(true);
	}

	public edit() {
		if (
			!this.parentForm.canSave ||
			this.parentForm.selectedRecordIsReadOnly
		) {
			return;
		}
		this.enableEditMode();
		this.classicPanel.focusFirstField();
		this.sendEvent(Slideout.Events.Edit);
		// Set view mode
		Logger.log('Form on edit mode');
	}

	/**
	 * Combine cancel and close into single method so one keyboard shortcut can be used for both
	 */
	public cancelEditOrClose() {
		if (!this.isInEditMode) {
			// NOTE: Jake: 2019-04-01 - WEBUI-1455
			// This logic is here so ESCAPE will close the slideout with the keyboard shortcut system.
			this.close();
			return;
		}

		this.classicPanel
			.promptToClosePanelIfChangedAndClearValidation()
			.then(() => this.enableReadonlyMode());
	}

	/**
	 * Handle delete for toggle-able XXz_Delete fields and hard delete
	 */
	public buttonToggleDelete() {
		this.parentForm.showDeleteRecordDialog();
	}

	public async showSlideout() {
		await this.classicPanel.waitUntilFunctionNameSet;
		this.open();
	}

	private readonly loadNotesDebounced = debounce(() => {
		if (!this.recordId) {
			// this.recordId can be null if user quickly click on contextMenu button and press escape
			return;
		}
		Logger.log('Getting notes for id:', this.recordId);
		this.notesApi
			.checkNotesData(this.functionName, this.recordId)
			.then((notesData) => {
				this.recordHasNotes = NotesApi.hasNotes(notesData);
			});
	}, DEBOUNCE_RECORD_SELECT_DURATION);

	// method was overridden by @focusable annotation
	private open(passive?: boolean) {
		if (this.isOpen()) {
			return;
		}

		if (!passive) {
			this.setIsOpen(true);
		}
	}

	// method was overridden by @focusable annotation
	public close(passive?: boolean) {
		if (!this.isOpen()) {
			return;
		}
		if (!passive) {
			this.classicPanel
				.promptToClosePanelIfChangedAndClearValidation()
				.then(() => {
					this.setIsOpen(false);
				});
		}
		this.sendEvent(Slideout.Events.CloseRecord);
	}

	public setCanClick(clickable: boolean) {
		this.canClick = clickable;
	}

	public setEnableDelete(enable: boolean) {
		this.enableDelete = enable;
	}

	// not needed because these properties are readonly?
	// public setCanSave(saveable: boolean) {
	// 	this.parentForm.canSave = saveable;
	// }
	// public setCanDelete(deletable: boolean) {
	// 	this.parentForm.canDelete = deletable;
	// }

	public setFunctionName(functionName: string) {
		this.functionName = functionName;
	}

	public showNotesDialog(): Promise<DialogCloseResult> | Promise<undefined> {
		if (!this.parentForm.hasNotes) {
			return Promise.resolve(undefined);
		}
		// do not show notes if no record is selected (webui-1068)
		if (!this.recordId) {
			Logger.log('Must select a record before notes can shown');
			return Promise.resolve(undefined);
		}
		const notesDialogModel: NotesDialogModel = {
			noteTitle: this.notesTitle,
			functionName: this.functionName,
			recordId: this.recordId,
			readOnly: this.parentForm.readOnlyForm,
		};
		return this.dialogUtils
			.showCustomDialog(NotesDialog, notesDialogModel)
			.whenClosed(
				(
					dialogCloseResult: DialogCloseResult & {
						output: NotesDialogResult;
					},
				) => {
					const output: NotesDialogResult = dialogCloseResult.output;
					if (output.haveNotesChanged) {
						if (this.classicPanel) {
							this.classicPanel.populateWithClassicFormData(
								this.functionName,
								this.recordId,
							);
						}
						this.loadNotesDebounced();
					}
					return dialogCloseResult;
				},
			);
	}

	@computedFrom('hasAuditDetailsOpen', 'parentForm.hasAuditDetails')
	public get canOpenAuditDetails(): boolean {
		return (
			!this.hasAuditDetailsOpen &&
			this.parentForm &&
			this.parentForm.hasAuditDetails
		);
	}

	@computedFrom(
		'enableDelete',
		'parentForm.readOnlyForm',
		'parentForm.selectedRecordIsReadOnly',
		'parentForm.isUnDeletedAvailable',
		'parentForm.isRecordDeleted',
	)
	get deleteEnabled(): boolean {
		return (
			this.enableDelete &&
			!this.parentForm.readOnlyForm &&
			!this.parentForm.selectedRecordIsReadOnly &&
			(this.parentForm.isUnDeletedAvailable ||
				!this.parentForm.isRecordDeleted)
		);
	}

	@computedFrom(
		'parentForm.readOnlyForm',
		'parentForm.selectedRecordIsReadOnly',
	)
	get editEnabled(): boolean {
		return (
			!this.parentForm.readOnlyForm &&
			!this.parentForm.selectedRecordIsReadOnly
		);
	}

	public showAuditDialog(): void {
		if (!this.canOpenAuditDetails) {
			return;
		}
		const auditDialogModel: AuditDialogModel = {
			functionName: this.functionName,
			recordId: this.recordId,
			auditApi: this.auditApi,
			columnNames: this.auditDialogColumnNames,
			tableConfig: this.auditDialogTableConfig,
			queryParams: this.queryParams,
		};
		this.hasAuditDetailsOpen = true;
		this.dialogUtils
			.showCustomDialog(AuditDialog, auditDialogModel)
			.whenClosed(() => {
				this.hasAuditDetailsOpen = false;
			});
	}

	public showAddressAuditDialog(): void {
		if (!this.canOpenAuditDetails) {
			return;
		}
		const auditDialogModel: AuditDialogModel = {
			functionName: this.functionName,
			recordId: this.recordId,
			auditApi: this.auditApi,
			columnNames: this.auditDialogColumnNames,
			tableConfig: this.auditDialogTableConfig,
			queryParams: this.queryParams,
			isAddressAudit: true,
		};
		this.hasAuditDetailsOpen = true;
		this.dialogUtils
			.showCustomDialog(AuditDialog, auditDialogModel)
			.whenClosed(() => {
				this.hasAuditDetailsOpen = false;
			});
	}

	public sendEvent(event: string, data?: any) {
		if (
			!Object.keys(Slideout.Events).some(
				(key) => Slideout.Events[key] === event,
			)
		) {
			Logger.warn(`Slideout.Events does not have such event: "${event}"`);
		}
		if (!this.canClick) {
			return;
		}
		this.eventAggregator.publish(event, data);
	}

	public isOpen(): boolean {
		return this.isSlideoutShown;
	}

	/**
	 * This takes a formModel and applies it to the slideout if it is open. Handy way to refresh
	 * the slideout data after a dialog performs an update such as soft deleting a record.
	 */
	public setFormModelIfOpen(formModel: FormModel): void {
		if (this.isOpen()) {
			if (!this.tableDataSet) {
				this.setFormModel(formModel);
			}
			this.enableReadonlyMode();
		}
	}

	@computedFrom('isSlideoutShown', 'classicPanel.isInEditMode')
	get isInEditMode() {
		return this.isSlideoutShown && this.classicPanel.isInEditMode;
	}

	set extraReadOnlyParameters(parameters: string[]) {
		this.classicPanel.extraReadOnlyParameters = parameters;
	}

	public set preSelectedParameters(parameters: FormParameters[]) {
		this.classicPanel.preSelectedParameters = parameters;
	}

	get panel() {
		return this.classicPanel;
	}

	public setQueryParams(queryParams: Map<string, string>) {
		this.queryParams = queryParams;
		this.classicPanel.setQueryParams(queryParams);
	}

	private setIsOpen(isOpen: boolean) {
		if (this.isSlideoutShown === isOpen) {
			return;
		}
		this.isSlideoutShown = isOpen;
		if (this.isSlideoutShown) {
			this.attachKeyboardShortcuts();
		} else {
			this.detachKeyboardShortcuts();
		}
	}

	public setRecordSlideoutSize(
		table: JQuery<HTMLTableElement>,
		visibleColumns: number,
	): void {
		// Adjust the width of the slideout according to visibleColumns
		const $table = $(table);
		if ($table.length === 0) {
			throw new Error('Cannot find "table#record-selector-table"');
		}
		// do not set slideout size if table height is zero, ie table not visible
		// if table is inside an expanding-card, defer setting size to when card is shown
		if ($table.parent().outerHeight() <= 0) {
			return;
		}
		const columnsWidth = $table
			.find('thead th')
			.map((_, element) => $(element).outerWidth())
			.get()
			.splice(0, visibleColumns)
			.reduce((a, b) => Number(a) + Number(b), 0);
		this.slideoutWidth =
			((window.innerWidth - $table.offset().left - columnsWidth) /
				window.innerWidth) *
			100;
	}

	/**
	 * Need to specify top of slideout relative to main element when full height as its position is fixed
	 */
	private setSlideoutTop() {
		const mainEl = this.refSlideoutEl.closest('.a-app__main');
		if (!mainEl) {
			Logger.warning('Unable to retrieve main element');
			return;
		}

		const mainHeaderEl: HTMLElement = mainEl.querySelector(
			'.a-app__main__header',
		);
		if (!mainHeaderEl) {
			Logger.warning('Unable to retrieve header element');
			return;
		}

		// without this, main element height is same as document height on first load of page
		window.requestAnimationFrame(() => {
			if (this.refSlideoutEl) {
				this.refSlideoutEl.style.top =
					document.documentElement.clientHeight -
					mainEl.clientHeight +
					mainHeaderEl.clientHeight +
					'px';
			}
		});
	}

	private refSlideoutElChanged(
		slideoutEl: HTMLElement,
		oldSlideoutEl: HTMLElement,
	) {
		if (this.refSlideoutEl) {
			// Initialize this once
			this.jQueryReference = $(this.refSlideoutEl);
			this.updateTooltip();
		}
	}

	private updateTooltip(): void {
		this.jQueryReference
			.find(
				[
					'li[data-toggle="tooltip"]',
					'button[data-toggle="tooltip"]',
					'a[data-toggle="tooltip"]',
				].join(','),
			)
			.tooltip({ delay: { show: 500, hide: 100 } });

		this.jQueryReference
			.find('input[data-toggle="tooltip"]')
			.tooltip({ container: 'body', delay: { show: 500, hide: 100 } });

		// NOTE: Jake: 2019-08-13 - WEBUI-2182
		// Hack to update tooltip titles to use label text.
		// We use this so that the tooltip for "Delete" says "Un-delete" for the tooltip.
		// A better solution might be to have our own tooltip system that supports
		// interpolation of variables. ie. `data-original-title="${textContent} (ALT+Delete)"`
		// 500ms was chosen because 0, 1, 10 and 50 were all way too short.
		setTimeout(() => {
			if (this.jQueryReference.length === 0) {
				return;
			}
			const elements = this.jQueryReference[0].querySelectorAll<
				HTMLAnchorElement | HTMLButtonElement
			>('a,button');
			for (const el of elements) {
				const title = el.getAttribute('data-original-title');
				if (!title) {
					continue;
				}
				const textContent = el.textContent.trim();
				if (!textContent) {
					continue;
				}
				const titleAndShortcuts = title.split('(', 2);
				if (titleAndShortcuts.length <= 1) {
					continue;
				}
				el.setAttribute(
					'data-original-title',
					textContent + ' (' + titleAndShortcuts[1],
				);
			}
		}, 500);
	}

	private onMouseEnter() {
		// NOTE: Jake: 2019-04-12 - WEBUI-1505
		// PerfectScrollbar doesn't calculate the correct height in "attached()" as nested templates in Aurelia don't all render
		// at the same time. We also don't have a solid way of determining when the form is completely ready / rendered.
		// To workaround this, we simply update the scrollbar on mouseenter.
		if (this.slideoutPS) {
			// NOTE: Jake: 2019-04-12 - WEBUI-1505
			// Using "requestAnimationFrame" reduces time to execute this update logic
			// from ~11ms to ~5ms. I'd ideally like this to be lower but this should be good enough performance wise.
			// If we do need to optimize this, I suggest we just either:
			// - Call "slideoutPS.update()" when the formModel is changed.
			// or
			// - Check the height of the slideout, cache it. If the height has changed, re-call update()
			// (ie. seperate reads/writes: http://wilsonpage.co.uk/preventing-layout-thrashing/)
			//
			// Keep in mind that I'm uncertain of if the latter method will offer gains! Make sure to profile with
			// Chrome DevTools as I did on the JIRA.
			window.requestAnimationFrame(() => {
				if (this.slideoutPS) {
					this.slideoutPS.update();
				}
			});
		}
	}

	@computedFrom(
		'parentForm.canDelete',
		'isInEditMode',
		'areStandardEditButtonsHidden',
	)
	private get isToggleDeleteButtonVisible() {
		return (
			!this.areStandardEditButtonsHidden &&
			this.parentForm.canDelete &&
			!this.isInEditMode
		);
	}

	@computedFrom(
		'parentForm.canSave',
		'isInEditMode',
		'classicPanel.cleanModel',
		'areStandardEditButtonsHidden',
	)
	public get isEditButtonVisible() {
		if (this.areStandardEditButtonsHidden) {
			return false;
		}
		if (!this.parentForm || !this.parentForm.canSave || this.isInEditMode) {
			return false;
		}
		// NOTE(Jae): 2020-03-30 - WEBUI-3046
		// We need to use the cleanModel as we currently change the "modifiable" field of
		// "formModel.fields" to Modifiable.FALSE when toggling between edit/not-editing.
		if (this.isCleanFormModelReadOnly()) {
			return false;
		}
		// Can edit this form
		return true;
	}

	@computedFrom('isInEditMode', 'areStandardEditButtonsHidden')
	public get isCancelButtonVisible() {
		return !this.areStandardEditButtonsHidden && this.isInEditMode;
	}

	@computedFrom(
		'parentForm.canSave',
		'isInEditMode',
		'areStandardEditButtonsHidden',
	)
	public get isSaveButtonVisible() {
		return (
			!this.areStandardEditButtonsHidden &&
			this.parentForm.canSave &&
			this.isInEditMode
		);
	}

	@computedFrom(
		'parentForm.slideoutContextMenu',
		'canOpenAuditDetails',
		'parentForm.recordSelectorTable.selectedObjId',
	)
	private get slideoutContextMenu(): ContextMenuItem[] {
		if (!this.parentForm) {
			return [];
		}
		// DEBUG: Jake: 2019-11-13
		// To assist in debugging unnecessary recomputes
		// console.warn(this.parentForm.constructor.name, 'compute:slideoutContextMenu', this.parentForm.slideoutContextMenu.length, this.parentForm.hasAuditDetails, this.parentForm.recordSelectorTable.selectedObjId)
		const result: ContextMenuItem[] = [];
		result.push(...this.parentForm.slideoutContextMenu);
		if (this.parentForm.hasAuditDetails) {
			result.push({
				id: 'slideoutAuditDetails',
				label: this.i18n.tr('clickable-elements.audit-details'),
				onClick: (e) => this.showAuditDialog(),
				disabled: !this.canOpenAuditDetails,
				iconClass: 'fa-history',
			});
		}
		return result;
	}

	set recordIdentity(recordId: number) {
		this.recordId = recordId;
	}

	public isCleanFormModelReadOnly(): boolean {
		return FormModelUtils.isExistingModelReadonly(
			this.classicPanel.cleanModel,
		);
	}
}
